import React, { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

const LANES = 3;
export const VIEW_AHEAD = 180;
/** Meters past the finish the banner stays visible (leaves the bottom of the view). */
export const FINISH_VIEW_BEHIND = 28;
const HORIZON_YFRAC = 0.08;
const FINISH_AT_CAR_YFRAC = 0.80;
const FINISH_PAST_YFRAC = 1.10;
export const VB_W = 240;
const VB_H = 360;
const SAMPLES = 18;
const TAPER_PERIOD = 720;
const TAPER_PERIOD_HARD = 560;
const BLEND_M = 48;
const DEFAULT_TRACK_LENGTH = { 1: 1600, 2: 2400, 3: 3200, 4: 4200, 5: 5400 };

/**
 * Visual + geometric profile per stage.
 * L1 is straight; L2 tapers; L3 winds; L4/L5 mix those styles along the track.
 * Palettes stay per-level even when geometry is mixed.
 */
export const ROAD_PROFILES = {
    1: {
        name: 'Meadow Run',
        roadside: 24,
        asphalt: '#1c2428',
        shoulder: '#4ECDC4',
        dash: '#d4e8e4',
        grassL: ['#0a2e28', '#124038'],
        grassR: ['#0a2e28', '#124038'],
        decor: 'trees',
        curveAmp: 0,
        curveFreq: 0.018,
        curveAmp2: 0,
        curveFreq2: 0.041,
        sky: '#0c1a24',
    },
    2: {
        name: 'Coastline',
        roadside: 26,
        asphalt: '#243038',
        shoulder: '#7ec8e3',
        dash: '#c5e4f3',
        grassL: ['#1a4a5c', '#0d6b7a'],
        grassR: ['#c2a36b', '#8a7048'],
        decor: 'palms',
        curveAmp: 0,
        curveFreq: 0.014,
        curveAmp2: 0,
        curveFreq2: 0.033,
        sky: '#123040',
    },
    3: {
        name: 'Mountain Pass',
        roadside: 34,
        asphalt: '#2a2624',
        shoulder: '#c4a574',
        dash: '#e8d5a3',
        grassL: ['#3d3834', '#5a534c'],
        grassR: ['#4a4038', '#2e2a26'],
        decor: 'rocks',
        curveAmp: 22,
        curveFreq: 0.022,
        curveAmp2: 8,
        curveFreq2: 0.055,
        sky: '#1a1614',
    },
    4: {
        name: 'Neon City',
        roadside: 30,
        asphalt: '#16181e',
        shoulder: '#ff6b9d',
        dash: '#7dffd4',
        grassL: ['#1a1024', '#2a1840'],
        grassR: ['#12101c', '#241830'],
        decor: 'lamps',
        curveAmp: 18,
        curveFreq: 0.016,
        curveAmp2: 7,
        curveFreq2: 0.032,
        sky: '#0a0814',
    },
    5: {
        name: 'Red Dunes',
        roadside: 38,
        asphalt: '#3a2a22',
        shoulder: '#ffb347',
        dash: '#ffe0a3',
        grassL: ['#5a3020', '#8a4a28'],
        grassR: ['#6a3820', '#3a2014'],
        decor: 'cacti',
        curveAmp: 26,
        curveFreq: 0.024,
        curveAmp2: 10,
        curveFreq2: 0.048,
        sky: '#2a1410',
    },
};

export const roadProfileForLevel = (level) => {
    const n = Math.max(1, Math.min(5, Math.round(Number(level) || 1)));
    return ROAD_PROFILES[n];
};

const clampLevel = (level) => Math.max(1, Math.min(5, Math.round(Number(level) || 1)));

const trackLenFor = (level, trackLength) => {
    const n = Number(trackLength);
    if (Number.isFinite(n) && n > 0) return n;
    return DEFAULT_TRACK_LENGTH[clampLevel(level)];
};

/** Numeric LCG — keep in sync with road_fighter_gm.seeded_rng. */
export const seededRng = (seed) => {
    let s = seed >>> 0;
    return () => {
        s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
        return s / 4294967296;
    };
};

const lerp = (a, b, t) => a + (b - a) * Math.max(0, Math.min(1, t));

const roadShift = (distAlong, profile) => (
    profile.curveAmp * Math.sin(distAlong * profile.curveFreq)
    + profile.curveAmp2 * Math.sin(distAlong * profile.curveFreq2 + 1.1)
);

/** Keyframes for a 3→2→1→3 pinch cycle (u in 0..1). Keep in sync with Python TAPER_KEYS. */
const TAPER_KEYS = [
    { u: 0.00, lanes: 3, left: 0, right: 3 },
    { u: 0.20, lanes: 3, left: 0, right: 3 },
    { u: 0.28, lanes: 2, left: 0, right: 2 },
    { u: 0.44, lanes: 2, left: 0, right: 2 },
    { u: 0.52, lanes: 1, left: 1, right: 2 },
    { u: 0.64, lanes: 1, left: 1, right: 2 },
    { u: 0.76, lanes: 3, left: 0, right: 3 },
    { u: 1.00, lanes: 3, left: 0, right: 3 },
];

export const taperState = (dist, period = TAPER_PERIOD) => {
    const p = Math.max(1, Number(period) || TAPER_PERIOD);
    const u = (((Number(dist) || 0) % p) + p) % p / p;
    let i = 0;
    while (i < TAPER_KEYS.length - 1 && u >= TAPER_KEYS[i + 1].u) i += 1;
    const a = TAPER_KEYS[i];
    const b = TAPER_KEYS[Math.min(i + 1, TAPER_KEYS.length - 1)];
    const span = b.u - a.u || 1;
    const t = (u - a.u) / span;
    const leftEdge = a.left + (b.left - a.left) * t;
    const rightEdge = a.right + (b.right - a.right) * t;
    return {
        laneCount: t < 0.5 ? a.lanes : b.lanes,
        leftEdge,
        rightEdge,
        scale: (rightEdge - leftEdge) / LANES,
    };
};

const STYLES = ['straight', 'taper', 'curvy'];
const _mixCache = new Map();

/**
 * Seeded, stable mix of straight / taper / curvy segments for levels 4–5.
 * Keep in sync with road_fighter_gm.mix_segments.
 */
export const mixSegments = (level, trackLength) => {
    const lv = clampLevel(level);
    const track = Math.round(trackLenFor(lv, trackLength));
    const key = `${lv}:${track}`;
    if (_mixCache.has(key)) return _mixCache.get(key);

    const rng = seededRng(lv * 99991 + track * 13);
    const minLen = lv >= 5 ? 280 : 420;
    const maxLen = lv >= 5 ? 520 : 800;
    const weights = lv >= 5 ? [0.15, 0.50, 1.0] : [0.34, 0.62, 1.0];
    const first = STYLES.slice();
    for (let i = first.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = first[i];
        first[i] = first[j];
        first[j] = tmp;
    }
    const pickLen = () => minLen + Math.floor(rng() * (maxLen - minLen + 1));
    const segs = [];
    let pos = 0;
    let prev = null;
    const pushSeg = (style, len) => {
        segs.push({ style, start: pos, length: len });
        prev = style;
        pos += len;
    };
    for (const style of first) {
        if (pos >= track) break;
        let len = pickLen();
        if (pos + len > track) len = track - pos;
        pushSeg(style, len);
    }
    while (pos < track) {
        const r = rng();
        let style = r < weights[0] ? 'straight' : r < weights[1] ? 'taper' : 'curvy';
        if (style === prev) {
            const avoid = prev;
            const opts = STYLES.filter((s) => s !== avoid);
            style = opts[Math.floor(rng() * opts.length)];
        }
        let len = pickLen();
        if (pos + len > track) len = track - pos;
        if (len < 100 && segs.length) {
            segs[segs.length - 1].length += len;
            break;
        }
        pushSeg(style, len);
    }
    _mixCache.set(key, segs);
    return segs;
};

const styleForLevel = (lv) => {
    if (lv === 2) return 'taper';
    if (lv === 3) return 'curvy';
    return 'straight';
};

const rawShapeForStyle = (dist, style, profile, localDist, taperPeriod) => {
    const shift = style === 'curvy' ? roadShift(dist, profile) : 0;
    if (style === 'taper') {
        const t = taperState(localDist, taperPeriod);
        return {
            shift,
            laneCount: t.laneCount,
            leftEdge: t.leftEdge,
            rightEdge: t.rightEdge,
            roadWidthScale: t.scale,
            style,
        };
    }
    return {
        shift,
        laneCount: 3,
        leftEdge: 0,
        rightEdge: 3,
        roadWidthScale: 1,
        style,
    };
};

const lookupSegment = (segs, dist) => {
    if (!segs.length) return { seg: { style: 'straight', start: 0, length: 1 }, index: 0 };
    const d = Math.max(0, dist);
    for (let i = 0; i < segs.length; i += 1) {
        if (d < segs[i].start + segs[i].length) return { seg: segs[i], index: i };
    }
    return { seg: segs[segs.length - 1], index: segs.length - 1 };
};

/**
 * Road geometry at a distance along a level.
 * `shift` / edges drive the SVG; `laneCount` drives pinch gameplay.
 */
export const roadShapeAt = (distance, level, trackLength) => {
    const lv = clampLevel(level);
    const profile = ROAD_PROFILES[lv];
    const dist = Number(distance) || 0;
    const track = trackLenFor(lv, trackLength);
    const taperPeriod = lv >= 5 ? TAPER_PERIOD_HARD : TAPER_PERIOD;

    if (lv <= 3) {
        const style = styleForLevel(lv);
        return rawShapeForStyle(dist, style, profile, dist, taperPeriod);
    }

    const segs = mixSegments(lv, track);
    const { seg, index } = lookupSegment(segs, dist);
    const local = dist - seg.start;
    const current = rawShapeForStyle(dist, seg.style, profile, local, taperPeriod);
    const blendWindow = Math.min(BLEND_M, Math.max(12, seg.length / 4));
    let shift = current.shift;
    let leftEdge = current.leftEdge;
    let rightEdge = current.rightEdge;

    if (index > 0 && local < blendWindow) {
        const prev = segs[index - 1];
        const prevLocal = dist - prev.start;
        const other = rawShapeForStyle(dist, prev.style, profile, prevLocal, taperPeriod);
        const t = local / blendWindow;
        shift = lerp(other.shift, current.shift, t);
        leftEdge = lerp(other.leftEdge, current.leftEdge, t);
        rightEdge = lerp(other.rightEdge, current.rightEdge, t);
    } else if (index < segs.length - 1 && seg.start + seg.length - dist < blendWindow) {
        const next = segs[index + 1];
        const nextLocal = dist - next.start;
        const other = rawShapeForStyle(dist, next.style, profile, nextLocal, taperPeriod);
        const t = (seg.start + seg.length - dist) / blendWindow;
        shift = lerp(other.shift, current.shift, t);
        leftEdge = lerp(other.leftEdge, current.leftEdge, t);
        rightEdge = lerp(other.rightEdge, current.rightEdge, t);
    }

    return {
        shift,
        laneCount: current.laneCount,
        leftEdge,
        rightEdge,
        roadWidthScale: (rightEdge - leftEdge) / LANES,
        style: seg.style,
    };
};

export const activeLanes = (distance, level, trackLength) => (
    roadShapeAt(distance, level, trackLength).laneCount
);

export const validLanesForCount = (laneCount) => {
    const n = Math.max(1, Math.min(3, Math.round(Number(laneCount) || 3)));
    if (n >= 3) return [0, 1, 2];
    if (n === 2) return [0, 1];
    return [1];
};

export const laneValidAt = (lane, distance, level, trackLength) => (
    validLanesForCount(activeLanes(distance, level, trackLength)).includes(
        Math.round(Number(lane) || 0),
    )
);

/** NES-style broken inner lane marks. Outer shoulders stay solid. */
export const LANE_DASH = {
    array: '12 16',
    width: 2.5,
    scroll: 1.4,
};

export const innerLaneDividerCount = (laneCount) => (
    Math.max(0, Math.min(2, Math.round(Number(laneCount) || 0) - 1))
);

/**
 * Project track end into the portrait view (same remaining-distance idea as obstacles).
 * Far remaining → horizon; remaining ~ 0 → across the lanes in front of the car;
 * after crossing, the bar continues toward / off the bottom.
 */
export function finishLineProjection(playerDistance, trackLength, viewAhead = VIEW_AHEAD) {
    const remaining = (Number(trackLength) || 0) - (Number(playerDistance) || 0);
    const ahead = Number(viewAhead) > 0 ? Number(viewAhead) : VIEW_AHEAD;
    const behind = FINISH_VIEW_BEHIND;
    const inView = remaining <= ahead && remaining >= -behind;
    let yFrac = HORIZON_YFRAC;
    if (remaining >= 0) {
        const u = 1 - Math.max(0, Math.min(1, remaining / ahead));
        yFrac = HORIZON_YFRAC + u * (FINISH_AT_CAR_YFRAC - HORIZON_YFRAC);
    } else {
        const u = Math.max(0, Math.min(1, -remaining / behind));
        yFrac = FINISH_AT_CAR_YFRAC + u * (FINISH_PAST_YFRAC - FINISH_AT_CAR_YFRAC);
    }
    return { remaining, inView, yFrac };
}

function VerticalMeter({
    value = 0,
    color = '#FF6B6B',
    topLabel,
    bottomLabel,
    ariaLabel,
}) {
    const pct = Math.round(Math.max(0, Math.min(100, Number(value) || 0)));
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 50,
                flexShrink: 0,
                alignSelf: 'stretch',
                py: 0.5,
            }}
        >
            <Typography variant="caption" sx={{ color, fontWeight: 800, fontSize: '0.65rem', lineHeight: 1.2, textAlign: 'center', maxWidth: 50 }}>
                {topLabel}
            </Typography>
            <Box
                role="progressbar"
                aria-label={ariaLabel}
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                sx={{
                    flex: 1,
                    width: 10,
                    minHeight: 140,
                    my: 0.75,
                    borderRadius: 5,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.12)',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: `${pct}%`,
                        bgcolor: color,
                        borderRadius: 5,
                    }}
                />
            </Box>
            {bottomLabel != null && bottomLabel !== '' && (
                <Typography variant="caption" sx={{ color: '#a1a1aa', fontSize: '0.65rem', textAlign: 'center', lineHeight: 1.15 }}>
                    {bottomLabel}
                </Typography>
            )}
        </Box>
    );
}

/** Vertical track progress: bottom = start, top = finish. */
export function TrackProgressMeter({
    value = 0,
    remainingM = 0,
    color = '#FF6B6B',
}) {
    const pct = Math.round(Math.max(0, Math.min(100, Number(value) || 0)));
    const remain = Math.max(0, Math.round(Number(remainingM) || 0));
    return (
        <VerticalMeter
            value={pct}
            color={color}
            topLabel={`${pct}%`}
            bottomLabel={`${remain} m`}
            ariaLabel="Level finishing"
        />
    );
}

/** Teal/green when full, amber mid-tank, red when low. */
export function fuelMeterColor(pct) {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    if (p >= 50) return '#2dd4bf';
    if (p >= 25) return '#ffb347';
    return '#ef4444';
}

/** Vertical fuel tank: fill grows from the bottom; 100% reaches the top. */
export function FuelMeter({ value = 100 }) {
    const pct = Math.round(Math.max(0, Math.min(100, Number(value) || 0)));
    const color = fuelMeterColor(pct);
    return (
        <VerticalMeter
            value={pct}
            color={color}
            topLabel={`Fuel ${pct}%`}
            bottomLabel="tank"
            ariaLabel={`Fuel ${pct}%`}
        />
    );
}

/**
 * NES Road Fighter–style top-down portrait road.
 * Appearance and winding change with `level`.
 */
export default function RoadFighterScene({
    lane = 1,
    distance = 0,
    obstacles = [],
    fuelPacks = [],
    healthPacks = [],
    level = 1,
    skidding = false,
    width = 280,
    height = 420,
    trackLength,
}) {
    const profile = roadProfileForLevel(level);
    const uid = `lv${clampLevel(level)}`;
    const scroll = (Number(distance) || 0) % 40;
    const playerDist = Number(distance) || 0;
    const roadside = profile.roadside;
    const roadW = VB_W - roadside * 2;
    const laneW = roadW / LANES;
    const track = trackLenFor(level, trackLength);

    const sampleAt = useCallback((ahead) => roadShapeAt(playerDist + ahead, level, track), [playerDist, level, track]);

    const edges = useMemo(() => {
        const left = [];
        const right = [];
        const marks = [];
        let prevCount = null;
        for (let i = 0; i <= SAMPLES; i += 1) {
            const y = (i / SAMPLES) * VB_H;
            const ahead = (1 - i / SAMPLES) * VIEW_AHEAD;
            const shape = sampleAt(ahead);
            left.push([roadside + shape.leftEdge * laneW + shape.shift, y]);
            right.push([roadside + shape.rightEdge * laneW + shape.shift, y]);
            if (prevCount != null && shape.laneCount !== prevCount) {
                marks.push({
                    y,
                    left: roadside + shape.leftEdge * laneW + shape.shift,
                    right: roadside + shape.rightEdge * laneW + shape.shift,
                    shrinking: shape.laneCount < prevCount,
                });
            }
            prevCount = shape.laneCount;
        }
        return { left, right, poly: [...left, ...right.slice().reverse()], marks };
    }, [sampleAt, roadside, laneW]);

    const laneCenterX = (l, yFrac) => {
        const ahead = (1 - yFrac) * VIEW_AHEAD;
        const shape = sampleAt(ahead);
        return roadside + laneW * (l + 0.5) + shape.shift;
    };

    const cars = useMemo(() => {
        const playerLane = Math.max(0, Math.min(LANES - 1, Number(lane) || 0));
        const list = (obstacles || [])
            .filter((o) => o && o.distance != null)
            .map((o, i) => {
                const rel = Number(o.distance) - playerDist;
                if (rel < -8 || rel > VIEW_AHEAD) return null;
                const t = 1 - Math.max(0, Math.min(1, (rel + 8) / (VIEW_AHEAD + 8)));
                return {
                    key: `obs-${i}-${o.lane}-${o.distance}`,
                    lane: Math.max(0, Math.min(LANES - 1, Number(o.lane) || 0)),
                    yFrac: 0.08 + t * 0.72,
                    color: ENEMY_PALETTE[i % ENEMY_PALETTE.length],
                    kind: o.kind === 'bike' || o.kind === 'truck' ? o.kind : 'car',
                };
            })
            .filter(Boolean);
        const packs = (fuelPacks || [])
            .filter((o) => o && o.distance != null)
            .map((o, i) => {
                const rel = Number(o.distance) - playerDist;
                if (rel < -8 || rel > VIEW_AHEAD) return null;
                const t = 1 - Math.max(0, Math.min(1, (rel + 8) / (VIEW_AHEAD + 8)));
                return {
                    key: `fuel-${i}-${o.lane}-${o.distance}`,
                    lane: Math.max(0, Math.min(LANES - 1, Number(o.lane) || 0)),
                    yFrac: 0.08 + t * 0.72,
                };
            })
            .filter(Boolean);
        const health = (healthPacks || [])
            .filter((o) => o && o.distance != null)
            .map((o, i) => {
                const rel = Number(o.distance) - playerDist;
                if (rel < -8 || rel > VIEW_AHEAD) return null;
                const t = 1 - Math.max(0, Math.min(1, (rel + 8) / (VIEW_AHEAD + 8)));
                return {
                    key: `health-${i}-${o.lane}-${o.distance}`,
                    lane: Math.max(0, Math.min(LANES - 1, Number(o.lane) || 0)),
                    yFrac: 0.08 + t * 0.72,
                };
            })
            .filter(Boolean);
        return { playerLane, list, packs, health };
    }, [lane, playerDist, obstacles, fuelPacks, healthPacks]);

    const finish = finishLineProjection(playerDist, track);
    const finishGeom = finish.inView ? (() => {
        const yFrac = finish.yFrac;
        const ahead = (1 - Math.max(0, Math.min(1, yFrac))) * VIEW_AHEAD;
        const shape = sampleAt(ahead);
        return {
            y: yFrac * VB_H,
            left: roadside + shape.leftEdge * laneW + shape.shift,
            right: roadside + shape.rightEdge * laneW + shape.shift,
            yFrac,
            showLabel: yFrac >= 0.5,
        };
    })() : null;

    const polyPts = edges.poly.map((p) => p.join(',')).join(' ');
    const playerOffRoad = !laneValidAt(cars.playerLane, playerDist, level, track);

    const dividerPolylines = [1, 2].map((div) => {
        const chunks = [];
        let current = [];
        for (let i = 0; i <= SAMPLES; i += 1) {
            const y = (i / SAMPLES) * VB_H;
            const ahead = (1 - i / SAMPLES) * VIEW_AHEAD;
            const shape = sampleAt(ahead);
            const x = roadside + laneW * div + shape.shift;
            const left = roadside + shape.leftEdge * laneW + shape.shift;
            const right = roadside + shape.rightEdge * laneW + shape.shift;
            if (x > left + 3 && x < right - 3) {
                current.push(`${x},${y}`);
            } else if (current.length) {
                chunks.push(current);
                current = [];
            }
        }
        if (current.length) chunks.push(current);
        return chunks;
    });

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <svg
                viewBox={`0 0 ${VB_W} ${VB_H}`}
                width={width}
                height={height}
                style={{ maxWidth: '100%', display: 'block', borderRadius: 12, background: profile.sky }}
                role="img"
                aria-label={`${profile.name} road visualization`}
            >
                <defs>
                    <linearGradient id={`rfGrassL-${uid}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={profile.grassL[0]} />
                        <stop offset="100%" stopColor={profile.grassL[1]} />
                    </linearGradient>
                    <linearGradient id={`rfGrassR-${uid}`} x1="1" y1="0" x2="0" y2="0">
                        <stop offset="0%" stopColor={profile.grassR[0]} />
                        <stop offset="100%" stopColor={profile.grassR[1]} />
                    </linearGradient>
                    <pattern id={`rfDash-${uid}`} width="8" height="28" patternUnits="userSpaceOnUse" patternTransform={`translate(0 ${scroll * 1.4})`}>
                        <rect x="3" y="0" width="2" height="14" fill={profile.dash} opacity="0.85" />
                    </pattern>
                    <pattern id={`rfFinish-${uid}`} width="12" height="10" patternUnits="userSpaceOnUse">
                        <rect width="6" height="5" fill="#111218" />
                        <rect x="6" width="6" height="5" fill="#f4f4f5" />
                        <rect y="5" width="6" height="5" fill="#f4f4f5" />
                        <rect x="6" y="5" width="6" height="5" fill="#111218" />
                    </pattern>
                </defs>

                <rect x="0" y="0" width={VB_W} height={VB_H} fill={`url(#rfGrassL-${uid})`} />
                <rect x={VB_W / 2} y="0" width={VB_W / 2} height={VB_H} fill={`url(#rfGrassR-${uid})`} />

                <RoadsideDecor profile={profile} distance={playerDist} level={level} />

                <polygon points={polyPts} fill={profile.asphalt} />

                {edges.left.slice(0, -1).map((pt, i) => (
                    <line
                        key={`sl-${i}`}
                        x1={pt[0]} y1={pt[1]}
                        x2={edges.left[i + 1][0]} y2={edges.left[i + 1][1]}
                        stroke={profile.shoulder} strokeWidth="3"
                    />
                ))}
                {edges.right.slice(0, -1).map((pt, i) => (
                    <line
                        key={`sr-${i}`}
                        x1={pt[0]} y1={pt[1]}
                        x2={edges.right[i + 1][0]} y2={edges.right[i + 1][1]}
                        stroke={profile.shoulder} strokeWidth="3"
                    />
                ))}

                {dividerPolylines.map((chunks, di) => chunks.map((pts, ci) => (
                    <polyline
                        key={`div-${di}-${ci}`}
                        points={pts.join(' ')}
                        fill="none"
                        stroke={profile.dash}
                        strokeWidth={LANE_DASH.width}
                        strokeDasharray={LANE_DASH.array}
                        strokeDashoffset={-(scroll * LANE_DASH.scroll)}
                        strokeLinecap="butt"
                    />
                )))}

                {finishGeom && (
                    <FinishLineBar
                        left={finishGeom.left}
                        right={finishGeom.right}
                        y={finishGeom.y}
                        yFrac={finishGeom.yFrac}
                        patternId={`rfFinish-${uid}`}
                        showLabel={finishGeom.showLabel}
                    />
                )}

                {edges.marks.map((m, i) => {
                    const mid = (m.left + m.right) / 2;
                    const dir = m.shrinking ? 1 : -1;
                    return (
                        <g key={`mark-${i}`} opacity="0.9">
                            <line
                                x1={m.left} y1={m.y} x2={m.right} y2={m.y}
                                stroke={profile.dash} strokeWidth="2.5" strokeDasharray="10 6"
                            />
                            <polyline
                                points={`${mid - 10},${m.y - 6 * dir} ${mid},${m.y + 2 * dir} ${mid + 10},${m.y - 6 * dir}`}
                                fill="none"
                                stroke={profile.dash}
                                strokeWidth="2"
                            />
                        </g>
                    );
                })}

                {cars.packs.map((p) => (
                    <FuelCan key={p.key} x={laneCenterX(p.lane, p.yFrac)} y={p.yFrac * VB_H} />
                ))}
                {(cars.health || []).map((p) => (
                    <HealthCrate key={p.key} x={laneCenterX(p.lane, p.yFrac)} y={p.yFrac * VB_H} />
                ))}

                {cars.list.map((c) => {
                    const Sprite = c.kind === 'bike' ? BikeSprite : c.kind === 'truck' ? TruckSprite : CarSprite;
                    return (
                        <Sprite
                            key={c.key}
                            x={laneCenterX(c.lane, c.yFrac)}
                            y={c.yFrac * VB_H}
                            body={c.color}
                            accent="#1a1a1a"
                        />
                    );
                })}

                <CarSprite
                    x={laneCenterX(cars.playerLane, 0.86) + ((skidding || playerOffRoad)
                        ? (cars.playerLane <= 0 ? -10 : cars.playerLane >= LANES - 1 ? 10 : cars.playerLane === 0 ? -8 : 8)
                        : 0)}
                    y={VB_H * 0.86}
                    body="#2cbb5d"
                    accent="#6ee7d8"
                    isPlayer
                    skidding={skidding || playerOffRoad}
                    skidDir={cars.playerLane <= 1 ? -1 : 1}
                />
            </svg>
        </Box>
    );
}

const ENEMY_PALETTE = ['#ef4743', '#ffa116', '#5b8def', '#e8b84a', '#c45c9e'];

/** Meters between decor slots. L1 trees are densest so a straight road still reads as motion. */
const DECOR_SPACING = {
    trees: 18,
    palms: 26,
    rocks: 30,
    lamps: 32,
    cacti: 28,
};

/**
 * Stable roadside props for the visible strip.
 * Seeded from level + distance-quantized slot so items never flicker while scrolling.
 */
export function layoutRoadsideDecor(distance, level, profile) {
    const lv = clampLevel(level);
    const dist = Number(distance) || 0;
    const roadside = Math.max(12, Number(profile?.roadside) || 24);
    const kind = profile?.decor || 'trees';
    const spacing = DECOR_SPACING[kind] || 24;
    const occupancy = kind === 'trees' ? 0.9 : 0.8;
    const viewBack = 32;
    const viewFwd = VIEW_AHEAD + 40;
    const first = Math.floor((dist - viewBack) / spacing) - 1;
    const last = Math.ceil((dist + viewFwd) / spacing) + 1;
    const items = [];
    const maxLeft = roadside - 5;
    const minRight = VB_W - roadside + 5;
    const shoulderW = Math.max(8, roadside - 8);

    for (let slot = first; slot <= last; slot += 1) {
        for (const side of [-1, 1]) {
            const rng = seededRng((lv * 104729 + slot * 224737 + (side < 0 ? 17 : 91)) >>> 0);
            if (rng() > occupancy) continue;
            const worldDist = slot * spacing + (rng() - 0.5) * spacing * 0.72;
            const rel = worldDist - dist;
            if (rel < -viewBack || rel > viewFwd) continue;
            const y = (1 - rel / VIEW_AHEAD) * VB_H + (rng() - 0.5) * 14;
            const scale = 0.62 + rng() * 0.78;
            const depth = rng();
            const jitter = (rng() - 0.5) * 7;
            const half = 7 * scale;
            let x;
            if (side < 0) {
                x = 3 + depth * shoulderW + jitter;
                x = Math.max(3 + half, Math.min(maxLeft - 1, x));
            } else {
                x = VB_W - 3 - depth * shoulderW + jitter;
                x = Math.max(minRight + 1, Math.min(VB_W - 3 - half, x));
            }
            items.push({
                key: `d-${lv}-${slot}-${side}`,
                x,
                y,
                scale,
                side,
                variant: rng(),
                kind,
            });
        }
    }
    return items;
}

function RoadsideDecor({ profile, distance, level }) {
    const items = useMemo(
        () => layoutRoadsideDecor(distance, level, profile),
        [distance, level, profile],
    );
    return items.map((item) => (
        <DecorSprite key={item.key} item={item} />
    ));
}

function DecorSprite({ item }) {
    const { x, y, scale, variant, kind } = item;
    const v = Number(variant) || 0;
    if (kind === 'palms') {
        const canopy = v > 0.5 ? '#1a6b48' : '#165c3e';
        return (
            <g transform={`translate(${x}, ${y}) scale(${scale})`}>
                <ellipse cx="0" cy="0" rx="9" ry="11" fill={canopy} />
                <ellipse cx="-6" cy="2" rx="6" ry="7" fill="#147a52" opacity="0.85" />
                <rect x="-2" y="8" width="4" height="11" fill="#6b4a28" />
            </g>
        );
    }
    if (kind === 'rocks') {
        const fill = v > 0.5 ? '#6a6058' : '#7a7068';
        return (
            <g transform={`translate(${x}, ${y}) scale(${scale})`}>
                <polygon points="-11,10 1,-8 11,12" fill={fill} />
            </g>
        );
    }
    if (kind === 'lamps') {
        const glow = v > 0.5 ? '#ffb4d4' : '#7dffd4';
        return (
            <g transform={`translate(${x}, ${y}) scale(${scale})`}>
                <rect x="-1.5" y="0" width="3" height="22" fill="#889" />
                <circle cx="0" cy="0" r="4" fill={glow} opacity="0.85" />
            </g>
        );
    }
    if (kind === 'cacti') {
        const fill = v > 0.45 ? '#2d6b3a' : '#3a7a44';
        return (
            <g transform={`translate(${x}, ${y}) scale(${scale})`}>
                <rect x="-2.5" y="0" width="5" height="16" rx="2" fill={fill} />
                <rect x="-6.5" y="4" width="4" height="7" rx="2" fill={fill} />
            </g>
        );
    }
    const canopy = v > 0.5 ? '#1a5c4a' : '#164e40';
    return (
        <g transform={`translate(${x}, ${y}) scale(${scale})`}>
            <ellipse cx="0" cy="0" rx="7" ry="9" fill={canopy} />
            <rect x="-2" y="6" width="4" height="6" fill="#3d2a1a" />
        </g>
    );
}

function FinishLineBar({ left, right, y, yFrac, patternId, showLabel }) {
    const width = Math.max(8, (Number(right) || 0) - (Number(left) || 0));
    const x = Number(left) || 0;
    const cy = Number(y) || 0;
    const bandH = 7 + Math.max(0, Math.min(1, Number(yFrac) || 0)) * 7;
    const postH = bandH + 12;
    return (
        <g data-testid="rf-finish-line" aria-label="Finish line">
            <rect
                x={x}
                y={cy - bandH / 2}
                width={width}
                height={bandH}
                fill={`url(#${patternId})`}
                stroke="#f4f4f5"
                strokeWidth="1.2"
            />
            <rect x={x - 3.5} y={cy - postH + 2} width="3.2" height={postH} rx="0.6" fill="#e11d48" />
            <rect x={x + width + 0.3} y={cy - postH + 2} width="3.2" height={postH} rx="0.6" fill="#e11d48" />
            <circle cx={x - 1.9} cy={cy - postH + 2} r="2.6" fill="#f4f4f5" />
            <circle cx={x + width + 1.9} cy={cy - postH + 2} r="2.6" fill="#f4f4f5" />
            {showLabel && (
                <text
                    x={x + width / 2}
                    y={cy - bandH / 2 - 6}
                    textAnchor="middle"
                    fontSize="9"
                    fontFamily="monospace"
                    fontWeight="bold"
                    fill="#f4f4f5"
                    stroke="#111218"
                    strokeWidth="0.6"
                    paintOrder="stroke"
                >
                    FINISH
                </text>
            )}
        </g>
    );
}

function FuelCan({ x, y }) {
    return (
        <g transform={`translate(${x}, ${y})`}>
            <ellipse cx="0" cy="8" rx="7" ry="3" fill="#000" opacity="0.35" />
            <rect x="-6" y="-8" width="12" height="16" rx="2" fill="#e8b84a" stroke="#fff3c4" strokeWidth="1" />
            <rect x="-3" y="-12" width="6" height="5" rx="1" fill="#c9a227" />
            <text x="0" y="4" textAnchor="middle" fontSize="8" fill="#1a1204" fontWeight="bold">F</text>
        </g>
    );
}

function HealthCrate({ x, y }) {
    return (
        <g transform={`translate(${x}, ${y})`}>
            <ellipse cx="0" cy="8" rx="7" ry="3" fill="#000" opacity="0.35" />
            <rect x="-7" y="-8" width="14" height="15" rx="2" fill="#1f9d55" stroke="#d1fae5" strokeWidth="1.2" />
            <rect x="-2" y="-5.5" width="4" height="10" rx="0.5" fill="#fff" />
            <rect x="-5.5" y="-1.5" width="11" height="4" rx="0.5" fill="#fff" />
        </g>
    );
}

export function CarSprite({ x, y, body, accent, isPlayer, skidding, skidDir = 1 }) {
    const w = isPlayer ? 22 : 20;
    const h = isPlayer ? 32 : 28;
    const tilt = skidding && isPlayer ? 16 * skidDir : 0;
    const rot = isPlayer ? tilt : 180;
    return (
        <g transform={`translate(${x}, ${y}) rotate(${rot})`}>
            {skidding && isPlayer && (
                <g opacity="0.7">
                    <ellipse cx={-10 * skidDir} cy={h / 2} rx="7" ry="3" fill="#d4d4d8" />
                    <ellipse cx={-16 * skidDir} cy={h / 2 + 4} rx="10" ry="4" fill="#a1a1aa" />
                    <ellipse cx={-6 * skidDir} cy={h / 2 + 6} rx="5" ry="2.5" fill="#e4e4e7" />
                </g>
            )}
            <ellipse cx="0" cy={h / 2 - 2} rx={w / 2 + 2} ry="4" fill="#000" opacity="0.4" />
            <rect x={-w / 2} y={-h / 2} width={w} height={h} rx="4" fill={body} stroke={accent} strokeWidth="1.5" />
            <rect x={-w / 2 + 3} y={-h / 2 + 5} width={w - 6} height={7} rx="1.5" fill="#0a1a22" opacity="0.85" />
            <rect x={-w / 2 + 3} y={h / 2 - 12} width={w - 6} height="5" rx="1" fill="#0a1a22" opacity="0.55" />
            <rect x={-w / 2 + 3} y={-h / 2 + 1} width="4" height="2.5" rx="0.5" fill="#e8fff8" opacity="0.9" />
            <rect x={w / 2 - 7} y={-h / 2 + 1} width="4" height="2.5" rx="0.5" fill="#e8fff8" opacity="0.9" />
            {isPlayer && (
                <rect x={-3} y={h / 2 - 5} width="6" height="2" rx="0.5" fill="#6ee7d8" opacity="0.8" />
            )}
        </g>
    );
}

export function BikeSprite({ x, y, body, accent }) {
    return (
        <g transform={`translate(${x}, ${y}) rotate(180)`}>
            <ellipse cx="0" cy="10" rx="7" ry="3" fill="#000" opacity="0.35" />
            <circle cx="0" cy="9" r="4.5" fill="#1a1a1a" stroke={accent} strokeWidth="1" />
            <circle cx="0" cy="-10" r="4.2" fill="#1a1a1a" stroke={accent} strokeWidth="1" />
            <rect x="-3.5" y="-8" width="7" height="16" rx="2" fill={body} stroke={accent} strokeWidth="1" />
            <rect x="-2.5" y="-6" width="5" height="4" rx="1" fill="#0a1a22" opacity="0.8" />
            <rect x="-6" y="-12" width="12" height="2" rx="1" fill={accent} />
            <rect x="-2" y="-13" width="4" height="2" rx="0.5" fill="#e8fff8" opacity="0.9" />
        </g>
    );
}

export function TruckSprite({ x, y, body, accent }) {
    const w = 28;
    const h = 44;
    return (
        <g transform={`translate(${x}, ${y}) rotate(180)`}>
            <ellipse cx="0" cy={h / 2 - 2} rx={w / 2 + 3} ry="5" fill="#000" opacity="0.4" />
            <rect x={-w / 2} y={-h / 2 + 10} width={w} height={h - 12} rx="2" fill={body} stroke={accent} strokeWidth="1.5" />
            <rect x={-w / 2 + 2} y={-h / 2} width={w - 4} height="16" rx="3" fill={body} stroke={accent} strokeWidth="1.5" />
            <rect x={-w / 2 + 5} y={-h / 2 + 3} width={w - 10} height="8" rx="1.5" fill="#0a1a22" opacity="0.85" />
            <rect x={-w / 2 + 4} y={-h / 2 + 1} width="5" height="2.5" rx="0.5" fill="#e8fff8" opacity="0.9" />
            <rect x={w / 2 - 9} y={-h / 2 + 1} width="5" height="2.5" rx="0.5" fill="#e8fff8" opacity="0.9" />
            <rect x={-w / 2 + 3} y={4} width={w - 6} height="10" rx="1" fill="#0a1a22" opacity="0.25" />
        </g>
    );
}
